import Tesseract from 'tesseract.js';

/**
 * AI Document Checker Service
 * Validates documents using OCR and AI analysis
 */

class DocumentAIService {
  constructor() {
    this.ocrWorker = null;
    this.pdfLib = null;
  }

  /**
   * Lazy load PDF.js library
   */
  async loadPdfLib() {
    if (!this.pdfLib) {
      try {
        const pdfjsLib = await import('pdfjs-dist/build/pdf');
        const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');
        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
        this.pdfLib = pdfjsLib;
      } catch (error) {
        console.error('Failed to load PDF.js:', error);
        throw new Error('PDF bibliotheek kon niet worden geladen');
      }
    }
    return this.pdfLib;
  }

  /**
   * Initialize OCR worker
   */
  async initOCR() {
    if (!this.ocrWorker) {
      this.ocrWorker = await Tesseract.createWorker('nld+eng+pol');
    }
    return this.ocrWorker;
  }

  /**
   * Extract text from PDF
   */
  async extractTextFromPDF(file) {
    try {
      const pdfLib = await this.loadPdfLib();
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfLib.getDocument({ data: arrayBuffer }).promise;

      let fullText = '';
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      if (!fullText.trim()) {
        throw new Error('Geen tekst gevonden in PDF. Mogelijk is het een gescande afbeelding.');
      }

      return fullText;
    } catch (error) {
      console.error('PDF extraction error:', error);
      throw error;
    }
  }

  /**
   * Extract text from document (handles both images and PDFs)
   */
  async extractText(file) {
    try {
      // Check if it's a PDF
      if (file.type === 'application/pdf') {
        return await this.extractTextFromPDF(file);
      }

      // Otherwise, use OCR for images
      const worker = await this.initOCR();
      const { data: { text } } = await worker.recognize(file);
      return text;
    } catch (error) {
      console.error('OCR extraction error:', error);
      throw new Error('Failed to extract text from document');
    }
  }

  /**
   * Detect document type from extracted text
   */
  detectDocumentType(text) {
    const lowerText = text.toLowerCase();

    // ID Document patterns
    if (lowerText.includes('identiteitskaart') ||
        lowerText.includes('identity card') ||
        lowerText.includes('dowód osobisty') ||
        lowerText.includes('rijbewijs') ||
        lowerText.includes('driver') ||
        lowerText.includes('passport')) {
      return 'id';
    }

    // VOG (Certificate of Conduct) patterns - Enhanced detection
    if (lowerText.includes('verklaring omtrent het gedrag') ||
        lowerText.includes('verklaring omtrent gedrag') ||
        lowerText.includes('v.o.g') ||
        lowerText.includes('vog') ||
        lowerText.includes('certificate of conduct') ||
        lowerText.includes('gedragsverklaring') ||
        lowerText.includes('justis') || // Issuing authority
        lowerText.match(/\b(VOG|V\.O\.G\.)\b/i)) {
      return 'VOG';
    }

    // Insurance patterns
    if (lowerText.includes('verzekering') ||
        lowerText.includes('insurance') ||
        lowerText.includes('polis') ||
        lowerText.includes('ubezpieczenie')) {
      return 'insurance';
    }

    // Contract patterns
    if (lowerText.includes('contract') ||
        lowerText.includes('overeenkomst') ||
        lowerText.includes('umowa')) {
      return 'contract';
    }

    // Project document patterns

    // Offerte (Quote) patterns
    if (lowerText.includes('offerte') ||
        lowerText.includes('quotation') ||
        lowerText.includes('prijsopgave') ||
        lowerText.includes('quote') ||
        lowerText.match(/\bofferte\s*nummer/i) ||
        lowerText.match(/\bquote\s*number/i)) {
      return 'offerte';
    }

    // Tekening (Drawing) patterns
    if (lowerText.includes('tekening') ||
        lowerText.includes('drawing') ||
        lowerText.includes('bouwtekening') ||
        lowerText.includes('bouwplan') ||
        lowerText.includes('technical drawing') ||
        lowerText.includes('schaal') && lowerText.includes('1:')) {
      return 'tekening';
    }

    // Werkbeschrijving (Work description) patterns
    if (lowerText.includes('werkbeschrijving') ||
        lowerText.includes('work description') ||
        lowerText.includes('bestek') ||
        lowerText.includes('werk omschrijving') ||
        lowerText.includes('scope of work')) {
      return 'werkbeschrijving';
    }

    // Leveringsbon (Delivery note) patterns
    if (lowerText.includes('leveringsbon') ||
        lowerText.includes('delivery note') ||
        lowerText.includes('afleverbon') ||
        lowerText.includes('pakbon') ||
        lowerText.includes('ontvangstbevestiging')) {
      return 'leveringsbon';
    }

    // Vierkantemeterstaat (Square meter statement) patterns
    if (lowerText.includes('vierkantemeterstaat') ||
        lowerText.includes('vierkante meter') ||
        lowerText.includes('m²') ||
        lowerText.includes('m2 staat') ||
        lowerText.includes('oppervlakte staat') ||
        lowerText.includes('square meter')) {
      return 'vierkantemeterstaat';
    }

    return 'other';
  }

  /**
   * Extract expiration date from text
   */
  extractExpirationDate(text) {
    // Common date patterns
    const datePatterns = [
      // DD-MM-YYYY or DD/MM/YYYY
      /geldig\s*tot:?\s*(\d{1,2}[-/]\d{1,2}[-/]\d{4})/i,
      /expires?:?\s*(\d{1,2}[-/]\d{1,2}[-/]\d{4})/i,
      /valid\s*until:?\s*(\d{1,2}[-/]\d{1,2}[-/]\d{4})/i,
      /wygasa:?\s*(\d{1,2}[-/]\d{1,2}[-/]\d{4})/i,
      // Just date patterns
      /(\d{1,2}[-/]\d{1,2}[-/]\d{4})/g
    ];

    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        return this.parseDate(match[1] || match[0]);
      }
    }

    return null;
  }

  /**
   * Parse date string to Date object
   */
  parseDate(dateString) {
    try {
      // Remove any extra whitespace
      dateString = dateString.trim();

      // Try DD-MM-YYYY or DD/MM/YYYY format
      const parts = dateString.split(/[-/]/);
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);

        if (day && month >= 0 && year) {
          return new Date(year, month, day);
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if document is expired
   */
  isExpired(expirationDate) {
    if (!expirationDate) return null; // Unknown

    const now = new Date();
    return expirationDate < now;
  }

  /**
   * Check if document expires soon (within 30 days)
   */
  expiresSoon(expirationDate) {
    if (!expirationDate) return false;

    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));

    return expirationDate <= thirtyDaysFromNow && expirationDate >= now;
  }

  /**
   * Validate document comprehensively
   */
  async validateDocument(imageFile, expectedType = null) {
    try {
      // Extract text using OCR
      const extractedText = await this.extractText(imageFile);

      // Detect document type
      const detectedType = this.detectDocumentType(extractedText);

      // Extract expiration date
      let expirationDate = this.extractExpirationDate(extractedText);

      // If no expiry date found, set default to +1 year from now
      // This is especially important for VOG and other documents where expiry isn't printed
      let defaultExpirySet = false;
      if (!expirationDate) {
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        expirationDate = oneYearFromNow;
        defaultExpirySet = true;
      }

      // Check validity
      const expired = this.isExpired(expirationDate);
      const expiringSoon = this.expiresSoon(expirationDate);

      // Determine status
      let status = 'valid';
      if (expired) {
        status = 'expired';
      } else if (expiringSoon) {
        status = 'expiringSoon';
      }

      // Type mismatch check
      const typeMismatch = expectedType && detectedType !== expectedType;

      return {
        success: true,
        extractedText,
        detectedType,
        expirationDate: expirationDate ? expirationDate.toISOString() : null,
        defaultExpirySet, // Flag to indicate default expiry was used
        status,
        expired,
        expiringSoon,
        typeMismatch,
        confidence: this.calculateConfidence(extractedText, detectedType, expirationDate),
        suggestions: this.generateSuggestions(status, typeMismatch, detectedType, expectedType, defaultExpirySet)
      };
    } catch (error) {
      console.error('Document validation error:', error);
      return {
        success: false,
        error: error.message,
        extractedText: null,
        detectedType: null,
        expirationDate: null,
        status: 'error',
        confidence: 0
      };
    }
  }

  /**
   * Calculate confidence score (0-100)
   */
  calculateConfidence(text, type, expirationDate) {
    let confidence = 0;

    // Text extraction quality (0-40 points)
    if (text && text.length > 50) {
      confidence += 40;
    } else if (text && text.length > 20) {
      confidence += 20;
    }

    // Type detection (0-30 points)
    if (type && type !== 'other') {
      confidence += 30;
    }

    // Expiration date found (0-30 points)
    if (expirationDate) {
      confidence += 30;
    }

    return confidence;
  }

  /**
   * Generate helpful suggestions based on validation results
   */
  generateSuggestions(status, typeMismatch, detectedType, expectedType, defaultExpirySet = false) {
    const suggestions = [];

    // Info about default expiry date being set
    if (defaultExpirySet) {
      suggestions.push({
        type: 'info',
        message: 'Geen vervaldatum gevonden. Standaard ingesteld op +1 jaar. Pas aan indien nodig.',
        messageEN: 'No expiry date found. Defaulted to +1 year. Adjust if needed.',
        messagePL: 'Nie znaleziono daty ważności. Ustawiono domyślnie na +1 rok. Dostosuj w razie potrzeby.'
      });
    }

    if (status === 'expired') {
      suggestions.push({
        type: 'error',
        message: 'Dit document is verlopen. Upload een nieuw geldig document.',
        messageEN: 'This document has expired. Please upload a new valid document.',
        messagePL: 'Ten dokument wygasł. Prześlij nowy ważny dokument.'
      });
    }

    if (status === 'expiringSoon') {
      suggestions.push({
        type: 'warning',
        message: 'Dit document verloopt binnenkort. Overweeg een nieuw document te uploaden.',
        messageEN: 'This document expires soon. Consider uploading a new one.',
        messagePL: 'Ten dokument wkrótce wygasa. Rozważ przesłanie nowego.'
      });
    }

    if (typeMismatch) {
      suggestions.push({
        type: 'warning',
        message: `Verwacht documenttype: ${expectedType}, Gedetecteerd: ${detectedType}. Controleer of je het juiste document hebt geüpload.`,
        messageEN: `Expected document type: ${expectedType}, Detected: ${detectedType}. Please verify you uploaded the correct document.`,
        messagePL: `Oczekiwany typ dokumentu: ${expectedType}, Wykryty: ${detectedType}. Sprawdź, czy przesłałeś właściwy dokument.`
      });
    }

    if (status === 'valid' && !typeMismatch) {
      suggestions.push({
        type: 'success',
        message: 'Document ziet er geldig uit!',
        messageEN: 'Document looks valid!',
        messagePL: 'Dokument wygląda na ważny!'
      });
    }

    return suggestions;
  }

  /**
   * Batch validate multiple documents
   */
  async validateDocuments(documents) {
    const results = [];

    for (const doc of documents) {
      const result = await this.validateDocument(doc.file, doc.expectedType);
      results.push({
        fileName: doc.file.name,
        ...result
      });
    }

    return results;
  }

  /**
   * Cleanup resources
   */
  async cleanup() {
    if (this.ocrWorker) {
      await this.ocrWorker.terminate();
      this.ocrWorker = null;
    }
  }
}

// Export singleton instance
const documentAI = new DocumentAIService();
export default documentAI;

